import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const TermsPage = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - Healthcare Triage Platform | CheckFirst</title>
        <meta name="description" content="CheckFirst Terms of Service for our HIPAA-compliant patient triage platform. Understand our healthcare triage software terms and clinical decision support guidelines." />
        <meta name="keywords" content="terms of service, healthcare platform agreement, patient triage terms, HIPAA compliance, clinical decision support, emergency room triage app" />
        
        <meta property="og:title" content="Terms of Service - Healthcare Triage Platform" />
        <meta property="og:description" content="CheckFirst Terms of Service for our HIPAA-compliant patient triage platform." />
        <meta property="og:url" content="https://www.checkfirstcare.com/terms" />
        <meta property="og:type" content="website" />
        
        <link rel="canonical" href="https://www.checkfirstcare.com/terms" />
      </Helmet>
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
      <Link 
        to="/" 
        className="inline-flex items-center text-primary hover:text-primary/80 mb-8 transition-colors"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Home
      </Link>

      <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
      <p className="text-muted-foreground mb-8">Effective Date: December 1, 2024</p>

      <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
        <section>
          <h2 className="text-2xl font-semibold mb-4">Agreement to Terms</h2>
          <p className="text-foreground/80 mb-4">
            By accessing or using CheckFirst, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use our service.
          </p>
          <p className="text-foreground/80">
            CheckFirst is an AI-powered triage platform founded by Ranvir Gill, designed to help reduce non-urgent emergency room visits and improve healthcare access. In 2025, we've reached 4,000+ patients in the tri-state area, helped home care agencies save 10,776 billable hours with an estimated ~$232,000 in revenue saved, and demonstrated a 39% reduction in unnecessary ER visits.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">About CheckFirst</h2>
          <p className="text-foreground/80">
            CheckFirst was created as a social-impact initiative to address healthcare accessibility and efficiency. Founded by Ranvir Gill, a student at The Haverford School with experience as an Economic Policy Analyst & Legislative Fellow for the Office of State Senator Timothy P. Kearney and as a Quality Data-Analyst at Care First Home Health.
          </p>
          <p className="text-foreground/80">
            Our platform has established partnerships with home care agencies across the tri-state area, now serving over 4,000 patients. We have helped agencies save 10,776 billable hours with ~$232,000 in estimated revenue preserved. We work closely with nursing professionals to optimize our AI triage model.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Service Description</h2>
          <p className="text-foreground/80 mb-4">
            CheckFirst provides AI-powered medical triage services to help users assess the urgency of their medical conditions. Our service:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>Offers preliminary health assessments based on user-provided symptoms</li>
            <li>Provides recommendations for appropriate levels of care</li>
            <li>Connects users with healthcare resources and facilities</li>
            <li>Helps reduce unnecessary emergency room visits</li>
            <li>Supports better healthcare decision-making</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Medical Disclaimer</h2>
          <div className="bg-destructive/10 border border-destructive/30 p-6 rounded-lg mb-4">
            <p className="text-foreground/80 mb-4">
              <strong>IMPORTANT:</strong> CheckFirst is not a substitute for professional medical advice, diagnosis, or treatment. Our AI triage system provides guidance but does not replace the expertise of licensed healthcare professionals.
            </p>
            <p className="text-foreground/80 mb-4">
              <strong>In case of emergency:</strong> If you are experiencing a life-threatening emergency, call 911 or go to the nearest emergency room immediately. Do not rely solely on CheckFirst for emergency situations.
            </p>
            <p className="text-foreground/80">
              Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition. Never disregard professional medical advice or delay seeking it because of information provided by CheckFirst.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">User Responsibilities</h2>
          <p className="text-foreground/80 mb-4">By using CheckFirst, you agree to:</p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>Provide accurate and complete information about your symptoms and health status</li>
            <li>Use the service for lawful purposes only</li>
            <li>Not misrepresent your identity or medical condition</li>
            <li>Maintain the confidentiality of your account credentials</li>
            <li>Follow the recommendations provided while exercising your own judgment</li>
            <li>Seek professional medical care when advised to do so</li>
            <li>Not use the service to diagnose or treat others</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">HIPAA Compliance and Privacy</h2>
          <p className="text-foreground/80 mb-4">
            CheckFirst is a HIPAA-covered entity and fully complies with the Health Insurance Portability and Accountability Act (HIPAA). Your health information is protected as Protected Health Information (PHI) under federal law. For complete details about our security measures and compliance practices, please visit our <Link to="/hipaa-compliance" className="text-primary hover:underline font-medium">HIPAA Compliance</Link> page.
          </p>
          
          <h3 className="text-xl font-semibold mb-3">Your HIPAA Rights</h3>
          <p className="text-foreground/80 mb-4">You have specific rights under HIPAA, including:</p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80 mb-4">
            <li>Right to access your Protected Health Information</li>
            <li>Right to request amendments to your PHI</li>
            <li>Right to an accounting of disclosures</li>
            <li>Right to request restrictions on use and disclosure</li>
            <li>Right to confidential communications</li>
            <li>Right to file a complaint if you believe your privacy rights have been violated</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3">Notice of Privacy Practices</h3>
          <p className="text-foreground/80 mb-4">
            Our <Link to="/privacy" className="text-primary hover:underline font-medium">Privacy Policy</Link> serves as our Notice of Privacy Practices as required by HIPAA. It describes how we may use and disclose your PHI, your rights, and our legal duties. Please review our Privacy Policy carefully.
          </p>

          <h3 className="text-xl font-semibold mb-3">Authorization for Use of PHI</h3>
          <p className="text-foreground/80 mb-4">
            By using CheckFirst, you authorize us to use your PHI for treatment, payment, and healthcare operations as described in our Privacy Policy. For any other uses or disclosures, we will obtain your specific written authorization.
          </p>

          <h3 className="text-xl font-semibold mb-3">Data Analytics and Quality Improvement</h3>
          <p className="text-foreground/80">
            We use clinical data analytics to improve service quality, link quality indicators with patient outcomes, and enhance our triage algorithms. This is done in compliance with HIPAA and uses either de-identified data or data permitted under healthcare operations. Our experience managing care quality for thousands of patients informs our continuous improvement efforts.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Consent to Treatment</h2>
          <p className="text-foreground/80 mb-4">
            By using CheckFirst's triage services, you acknowledge and consent to the following:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>You understand that CheckFirst provides AI-powered triage assessment, not diagnosis or treatment</li>
            <li>You consent to the collection and use of your health information for triage purposes</li>
            <li>You understand that triage recommendations are not a substitute for professional medical evaluation</li>
            <li>You agree to provide accurate and complete health information</li>
            <li>You understand that in emergencies, you should call 911 or go to the nearest emergency room</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Intellectual Property</h2>
          <p className="text-foreground/80 mb-4">
            All content, features, and functionality of CheckFirst, including but not limited to text, graphics, logos, icons, images, and software, are the exclusive property of CheckFirst and are protected by copyright, trademark, and other intellectual property laws.
          </p>
          <p className="text-foreground/80">
            You may not reproduce, distribute, modify, or create derivative works from any content without our express written permission.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Limitation of Liability</h2>
          <p className="text-foreground/80 mb-4">
            To the fullest extent permitted by law, CheckFirst, its founder Ranvir Gill, and its partners shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>Your use or inability to use the service</li>
            <li>Any errors or omissions in the content or recommendations</li>
            <li>Unauthorized access to your personal information</li>
            <li>Any health outcomes related to decisions made based on our service</li>
            <li>Delays or interruptions in service</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Accuracy of Information</h2>
          <p className="text-foreground/80">
            While we strive to provide accurate and up-to-date health information and triage recommendations based on clinical best practices and data analytics, we cannot guarantee the accuracy, completeness, or usefulness of any information provided. Healthcare is complex, and individual circumstances vary significantly.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Service Modifications</h2>
          <p className="text-foreground/80">
            We reserve the right to modify, suspend, or discontinue any part of CheckFirst at any time without notice. We may also update our AI triage algorithms and features to improve service quality and effectiveness.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Third-Party Services</h2>
          <p className="text-foreground/80">
            CheckFirst may contain links to third-party websites or services, including healthcare providers and emergency services. We are not responsible for the content, privacy policies, or practices of any third-party sites.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Termination</h2>
          <p className="text-foreground/80">
            We reserve the right to terminate or suspend your access to CheckFirst at any time, without notice, for conduct that we believe violates these Terms of Service or is harmful to other users, us, or third parties, or for any other reason.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Governing Law</h2>
          <p className="text-foreground/80">
            These Terms of Service shall be governed by and construed in accordance with the laws of the Commonwealth of Pennsylvania, without regard to its conflict of law provisions.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Changes to Terms</h2>
          <p className="text-foreground/80">
            We may update these Terms of Service from time to time. We will notify you of any significant changes by posting the new terms on this page and updating the "Last updated" date. Your continued use of CheckFirst after such changes constitutes acceptance of the new terms.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Contact Information</h2>
          <p className="text-foreground/80 mb-4">
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className="bg-accent/50 p-6 rounded-lg">
            <p className="text-foreground/80 mb-2"><strong>CheckFirst</strong></p>
            <p className="text-foreground/80 mb-2">Founder: Ranvir Gill</p>
            <p className="text-foreground/80 mb-2">The Haverford School, Haverford, PA</p>
            <p className="text-foreground/80 mb-2">Email: ranvgill@haverford.org</p>
            <p className="text-foreground/80 text-sm mt-4">
              Background: National Merit Semifinalist, Economic Policy Analyst & Legislative Fellow for PA State Senator Timothy P. Kearney, Quality Data-Analyst at Care First Home Health
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4">Acknowledgment</h2>
          <p className="text-foreground/80">
            By using CheckFirst, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy. You also acknowledge that CheckFirst is a social-impact initiative aimed at improving healthcare access and reducing unnecessary emergency room visits, and that it should be used as a supportive tool in conjunction with professional medical care.
          </p>
        </section>
      </div>
    </div>
    </>
  );
};

export default TermsPage;
